// Map legend (spec 15c §3.3): relation colours always; markers, system types and fleets when open.
// The open state is remembered per browser.
import { ChevronDown, ChevronUp } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { FLEET_MARKER, HOME_MARKER, TYPE_ICONS } from '../../map/glyphs';
import { MAP_COLORS } from '../../theme';
import { Glyph } from './Glyph';

const KEY = 'map-legend-open';
const hex = (color: number) => `#${color.toString(16).padStart(6, '0')}`;
/** An example emblem colour for the "Other alliance" ring. */
const EXAMPLE_ALLIANCE = 0xf472b6;

function readOpen(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

function writeOpen(open: boolean) {
  try {
    localStorage.setItem(KEY, open ? '1' : '0');
  } catch {
    // Storage blocked: the legend just forgets its state.
  }
}

const Dot = ({ color }: { color: number }) => (
  <span className="size-2.5 rounded-full" style={{ backgroundColor: hex(color) }} />
);
const Ring = ({ color }: { color: number }) => (
  <span className="size-3 rounded-full border" style={{ borderColor: hex(color) }} />
);
const Dash = ({ color }: { color: number }) => (
  <span className="w-4 border-t-2 border-dashed" style={{ borderColor: hex(color) }} />
);

function Entry({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <li className="flex items-center gap-1.5">
      {icon}
      {label}
    </li>
  );
}

export function MapLegend({ hasAlliance }: { hasAlliance: boolean }) {
  const [open, setOpen] = useState(readOpen);
  const toggle = () => {
    setOpen(!open);
    writeOpen(!open);
  };
  const label = hex(MAP_COLORS.label);
  return (
    <div
      data-map-legend
      className="absolute top-2 left-2 z-30 max-w-[calc(100%-4.5rem)] hud-cut-sm bg-panel/80 px-3 py-1.5 text-[11px] text-muted backdrop-blur"
    >
      <div className="flex items-start gap-2">
        <ul className="flex flex-wrap gap-x-3 gap-y-1">
          <Entry icon={<Dot color={MAP_COLORS.own} />} label="You" />
          {hasAlliance ? <Entry icon={<Dot color={MAP_COLORS.ally} />} label="Allies" /> : null}
          <Entry icon={<Dot color={MAP_COLORS.player} />} label="Other commanders" />
          <Entry icon={<Dot color={MAP_COLORS.unowned} />} label="Unowned" />
        </ul>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-label={open ? 'Hide legend' : 'Show legend'}
          className="-my-3 -mr-3 inline-flex min-h-11 min-w-11 items-center justify-center text-info"
        >
          {open ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
        </button>
      </div>
      {open ? (
        <ul className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 border-t border-line pt-1.5">
          <Entry
            icon={<Glyph points={HOME_MARKER} color={hex(MAP_COLORS.own)} filled={false} />}
            label="Home system"
          />
          <Entry icon={<Ring color={MAP_COLORS.own} />} label="Your colony" />
          <Entry icon={<Ring color={MAP_COLORS.shield} />} label="Beginner shield" />
          <Entry icon={<Ring color={EXAMPLE_ALLIANCE} />} label="Other alliance" />
          <Entry
            icon={<Glyph points={TYPE_ICONS.resource_rich.points} color={label} />}
            label="Resource-Rich"
          />
          <Entry
            icon={<Glyph points={TYPE_ICONS.strategic.points} color={label} />}
            label="Strategic"
          />
          <Entry
            icon={<Glyph points={TYPE_ICONS.barren.points} color={label} filled={false} />}
            label="Barren"
          />
          <Entry
            icon={<Glyph points={FLEET_MARKER} color={hex(MAP_COLORS.own)} />}
            label="Your fleet"
          />
          {hasAlliance ? (
            <Entry icon={<Dash color={MAP_COLORS.ally} />} label="Alliance fleet" />
          ) : null}
          <Entry icon={<Dash color={MAP_COLORS.hostile} />} label="Hostile fleet" />
        </ul>
      ) : null}
    </div>
  );
}
